import type { Product, ProductSellingPrice } from "@cabocheck/shared";
import classNames from "classnames";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { CaboMarkdown } from "~/features/core/CaboMarkdown";
import { ParameterFaqs } from "~/features/products/ParameterFaqs";
import { formatPrice } from "~/utils/formatPrice";

type POCParameterFaq = {
  question?: string | null;
  response?: string | null;
};

type POCParameter = {
  medical_test_parameter_guid: string;
  name: string;
  faqs?: POCParameterFaq[];
};

type POCParameterWithFaqs = POCParameter & {
  faqs: POCParameterFaq[];
};

type PublicProductParameter = {
  medical_test_parameter_guid: string;
  name: string;
  faqs?: POCParameterFaq[] | null;
};

function hasFaqs(parameter: PublicProductParameter | POCParameter): parameter is POCParameterWithFaqs {
  return "faqs" in parameter && Array.isArray(parameter.faqs) && parameter.faqs.length > 0;
}

type PublicPocDetailProduct = {
  product_guid: string;
  name: string;
  description?: string;
  instructions?: string;
  specimen_type?: ProductSellingPrice["specimen_type"];
  medical_test_parameters?: Product["medical_test_parameters"] | PublicProductParameter[];
  selling_price?: number;
};

export function PublicPocProductDetail({
  product,
  backFallbackTo,
  cta,
}: {
  product: PublicPocDetailProduct;
  backFallbackTo: string;
  cta?: ReactNode;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const backTo = typeof location.state?.from === "string" ? location.state.from : backFallbackTo;

  const specimenTypeLabelMap: Record<string, string> = {
    BLOOD: t("Kapillarblutentnahme"),
    SALIVA: t("Speichelprobe"),
    URINE: t("Urinprobe"),
    STOOL: t("Stuhlprobe"),
  };
  const specimenTypeLabel = specimenTypeLabelMap[product.specimen_type ?? "BLOOD"] ?? specimenTypeLabelMap.BLOOD;
  const medicalTestParameters: PublicProductParameter[] = Array.isArray(product.medical_test_parameters)
    ? (product.medical_test_parameters as PublicProductParameter[])
    : [];
  const parametersWithFaqs = medicalTestParameters.filter(hasFaqs);

  return (
    <div className="public-container pt-20" id="angebot">
      <Link to={backTo} className="mt-12 text-center text-dynamic-primary sm:text-left">
        {t("Zurück zur Testübersicht")}
      </Link>
      <section className="grid gap-6 pb-10 pt-16">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={classNames("rounded-full px-4 py-2 text-sm font-semibold text-white", {
              "bg-dynamic-primary": product.specimen_type === "BLOOD" || !product.specimen_type,
              "bg-green-500": product.specimen_type === "URINE",
              "bg-yellow-500": product.specimen_type === "SALIVA",
              "bg-amber-800": product.specimen_type === "STOOL",
            })}
          >
            {specimenTypeLabel}
          </span>
          {product.selling_price != null ? (
            <span className="text-xl font-bold">{formatPrice(product.selling_price)} €</span>
          ) : null}
        </div>
        <div className="space-y-3">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{product.name}</h1>
          {product.description ? (
            <CaboMarkdown className="max-w-3xl text-gray-700">{product.description}</CaboMarkdown>
          ) : null}
        </div>
      </section>
      {medicalTestParameters.length ? (
        <section className="py-10">
          <h2 className="mb-4 text-2xl font-bold">{t("Diese Werte sind enthalten")}</h2>
          <div className="flex flex-wrap gap-3">
            {medicalTestParameters.map((parameter) => (
              <span
                key={parameter.medical_test_parameter_guid}
                className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700"
              >
                {parameter.name}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      {parametersWithFaqs.length ? (
        <section className="grid gap-8 py-10">
          {parametersWithFaqs.map((parameter) => (
            <ParameterFaqs key={parameter.medical_test_parameter_guid} parameter={parameter} />
          ))}
        </section>
      ) : null}
      {cta ? <div className="py-10">{cta}</div> : null}
      <div className="h-12" />
    </div>
  );
}
